import type { PartyStatus } from 'components/status-tag';
import type {
  InterestDetailFields,
  InterestFields,
  InterestStatus,
} from './types-interest';

// ---------------------------------------------------------------------------
// DO Interest Review display helpers — single source of truth for the
// status→StatusTag mapping and the interest-value formatting/parsing shared by
// the overview table, the detail view, and the edit panel (so labels/precision
// never drift). Mirrors the small helpers in identification-utils / cost-utils.
// ---------------------------------------------------------------------------

/**
 * Statuses that render a line muted: a line-level soft-delete here (`removed`)
 * or a well removed in the Wells Information section (`well_removed`). Muted
 * lines are struck-through and excluded from the section counts/tiles. Single
 * source of truth for the "is this line muted?" test so the status mapping,
 * section-status derivation, and row styling can never drift.
 * @param {InterestStatus} status - The DO interest lifecycle status.
 * @return {boolean} True when the line should render muted.
 */
export const isMutedInterestStatus = (status: InterestStatus): boolean =>
  status === 'removed' || status === 'well_removed';

/**
 * Map the DO interest status vocab onto the shared StatusTag `PartyStatus`.
 * The AFE `toPartyStatus` helper doesn't know `confirmed`/`edited`, so DO owns
 * its own mapping: confirmed → complete, edited → in_progress,
 * not_started → not_started, removed/well_removed → removed (both muted; a
 * well-removed line reuses the removed tag rather than inventing a new label).
 * @param {InterestStatus} status - The DO interest lifecycle status.
 * @return {PartyStatus} The StatusTag status to render.
 */
export const toPartyStatus = (status: InterestStatus): PartyStatus => {
  switch (status) {
    case 'confirmed':
      return 'complete';
    case 'edited':
      return 'in_progress';
    case 'removed':
    case 'well_removed':
      return 'removed';
    default:
      return 'not_started';
  }
};

const INTEREST_STATUS_LABELS: Record<InterestStatus, string> = {
  confirmed: 'Confirmed',
  edited: 'Edited',
  not_started: 'Not Started',
  removed: 'Removed',
  well_removed: 'Well Removed',
};

/**
 * Human label for a DO interest status. SSOT for the text form (the chip form is
 * `toPartyStatus`); unlike the chip, this keeps `removed` and `well_removed`
 * distinct so a reader can see why a line is muted.
 * @param {InterestStatus} status - The DO interest lifecycle status.
 * @return {string} The display label.
 */
export const interestStatusLabel = (status: InterestStatus): string =>
  INTEREST_STATUS_LABELS[status] ?? status;

/**
 * Interest Review section-status derivation — the SINGLE source of truth shared
 * by the overview section header (StatusTag) and the shell's left-nav tree node,
 * so the two can never disagree. Muted lines — soft-deleted (`removed`) or whose
 * well was removed in the Wells section (`well_removed`) — are excluded from BOTH
 * the numerator and the denominator (matching the "Complete" semantics: N
 * confirmed of M active lines); a set with no active lines is `not_started`.
 * Previously the shell counted removed lines in its denominator while the section
 * filtered them out, so 4 confirmed + 1 removed showed the tree "In Progress" but
 * the section header "Complete".
 * @param {{status: InterestStatus}[]} interests - The interest line set.
 * @return {{status: PartyStatus, confirmedCount: number, totalCount: number}}
 *   The section status plus active confirmed/total counts for the progress label.
 */
export const deriveInterestSectionStatus = (
  interests: readonly { status: InterestStatus }[]
): { status: PartyStatus; confirmedCount: number; totalCount: number } => {
  const active = interests.filter((i) => !isMutedInterestStatus(i.status));
  const confirmedCount = active.filter((i) => i.status === 'confirmed').length;
  const totalCount = active.length;
  const status: PartyStatus =
    confirmedCount === 0
      ? 'not_started'
      : confirmedCount === totalCount
        ? 'complete'
        : 'in_progress';
  return { status, confirmedCount, totalCount };
};

/**
 * Numeric string for a Decimal Ownership Interest (Interest__c). DO interest
 * values carry long fractional tails (e.g. 0.00123456, 8 dp); the previous
 * `toFixed(6)` rounded them off, so the displayed/edited value no longer matched
 * the document. `toFixed(10)` covers the domain precision (well past the standard
 * 8 dp) and the `Number(...)` wrapper both masks float64 representation noise
 * (0.1+0.2 → 0.3) and drops padding zeros (0.5 → "0.5", 0.00123456 →
 * "0.00123456"). Wrapping in Number() is the lint-approved use of toFixed for a
 * numeric value (see no-restricted-syntax rule).
 * @param {number} value - The raw interest fraction.
 * @return {string} The numeric string, full precision, no trailing zeros.
 */
const interestNumericString = (value: number): string =>
  `${Number(value.toFixed(10))}`;

/**
 * Interest display — full precision, trailing zeros trimmed, em-dash when nullish.
 * @param {number|null|undefined} value - The raw interest fraction.
 * @return {string} Formatted interest, or an em-dash when nullish/NaN.
 */
export const formatInterest = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return interestNumericString(value);
};

/**
 * Interest edit-field seed — the full-precision numeric string, blank for
 * null/NaN. Seeding at full precision (not rounded) is what prevents a Save
 * without a retype from persisting a truncated value back into Interest__c.
 * @param {number|null|undefined} value - The raw interest fraction.
 * @return {string} The numeric string to seed the input; blank when nullish/NaN.
 */
export const interestInputValue = (
  value: number | null | undefined
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return interestNumericString(value);
};

/**
 * Parse a free-typed interest string to a number. Blank → null (clears the
 * field); an unparseable non-blank string → undefined so the caller can reject
 * the edit and restore the last value instead of writing NaN.
 * @param {string} raw - The free-typed field value.
 * @return {number|null|undefined} Parsed number; null when blank; undefined when unparseable.
 */
export const parseInterest = (raw: string): number | null | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// ---------------------------------------------------------------------------
// Interest edit/create field mapping + payload building. Single source of truth
// for the panel's field labels and the camelCase↔snake_case request mapping, so
// the form and the payload can never drift. Exported (with
// buildInterestRequestBody) so the payload contract is unit-testable without
// rendering the Carbon SidePanel.
// ---------------------------------------------------------------------------

export interface InterestFieldDef {
  /** camelCase working-copy key. */
  key: keyof InterestDetailFields;
  label: string;
  /** snake_case request key. */
  requestKey: keyof InterestFields;
  /** `interest` is numeric; the rest are free-text. */
  numeric?: boolean;
  /**
   * Property identity — settable on CREATE (a user-added line names its
   * property), but read-only in EDIT mode: the update path
   * (Document::DivisionOrder::INTEREST_UPDATABLE_FIELDS) does not accept these columns, so an
   * edit would silently revert. Rendered read-only for context in edit mode.
   */
  createOnly?: boolean;
}

// `do_property_id` is deliberately NOT a field. It maps to DOProperty__c — the FK
// that resolves the line's well/UWI and its catalog property linkage. The overview
// row never carries it, so seeding it from the row would seed '' and a Save would
// blank DOProperty__c (severing the UWI + property linkage). It is not a
// user-editable value, so the panel never sends it and the edit path leaves the
// existing link untouched (Document::DivisionOrder#interest_mapped_attributes
// leaves omitted keys as-is).
export const INTEREST_FIELDS: InterestFieldDef[] = [
  { key: 'interest', label: 'Interest', requestKey: 'interest', numeric: true },
  { key: 'interestType', label: 'Interest Type', requestKey: 'interest_type' },
  { key: 'owner', label: 'Owner', requestKey: 'owner' },
  { key: 'operator', label: 'Operator', requestKey: 'operator' },
  {
    key: 'propertyName',
    label: 'Property Name',
    requestKey: 'property_name',
    createOnly: true,
  },
  {
    key: 'propertyNumber',
    label: 'Property Number',
    requestKey: 'property_number',
    createOnly: true,
  },
];

/**
 * Seed one edit-field's string value from the line detail (blank for create).
 * @param {InterestFieldDef} def - The field definition.
 * @param {InterestDetailFields|undefined} detail - The line's current values.
 * @return {string} The string to seed the input with.
 */
export const seedInterestValue = (
  def: InterestFieldDef,
  detail: InterestDetailFields | undefined
): string => {
  if (!detail) return '';
  if (def.numeric) return interestInputValue(detail.interest);
  const raw = detail[def.key];
  return raw === null || raw === undefined ? '' : String(raw);
};

const blankInterestValue = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

/**
 * Build the snake_case `interest` request body from the panel's working copy.
 * EDIT mode submits ONLY the fields the user changed and NEVER the property-
 * identity (createOnly) fields — so an edit can't silently revert a read-only
 * field, and (since do_property_id is not a field at all) can't blank the
 * DOProperty__c FK. CREATE mode submits the full field set. Returns `{ error }`
 * when the numeric interest is unparseable, so the caller can reject the save.
 * @param {'edit'|'create'} mode - The panel mode.
 * @param {Record<string,string>} values - The current working-copy values.
 * @param {Record<string,string>} seeded - The seeded baseline (change detection).
 * @return {{interest: InterestFields}|{error: string}} The request body or an error.
 */
export const buildInterestRequestBody = (
  mode: 'edit' | 'create',
  values: Record<string, string>,
  seeded: Record<string, string>
): { interest: InterestFields } | { error: string } => {
  const interest: InterestFields = {};
  for (const def of INTEREST_FIELDS) {
    // Property identity is read-only in edit mode — never submit it.
    if (mode === 'edit' && def.createOnly) continue;

    const raw = values[def.key] ?? '';

    // Edit mode: submit ONLY changed fields (the backend leaves omitted columns
    // untouched). Create mode has no baseline, so every field is submitted.
    if (mode === 'edit' && raw === (seeded[def.key] ?? '')) continue;

    if (def.numeric) {
      const parsed = parseInterest(raw);
      if (parsed === undefined) return { error: 'Interest must be a number.' };
      interest.interest = parsed;
    } else {
      interest[def.requestKey as Exclude<keyof InterestFields, 'interest'>] =
        blankInterestValue(raw);
    }
  }
  return { interest };
};
